import { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva } from 'class-variance-authority';

import { XIcon } from 'components/Blocks/Icons';

import { cn } from 'styles';
import { toastClassesByType } from './styles';

export const ToastProvider = ToastPrimitives.Provider;

export type ToastVariants = 'background' | 'error' | 'success' | 'primary' | 'warning' | 'info';

const toastVariants = cva(
  [
    'group pointer-events-auto relative flex w-full items-center gap-3 overflow-hidden rounded-md border p-2 pl-3 pr-8 shadow-lg transition-all',
    // open:
    'data-[state=open]:sm:slide-in-from-bottom-full repeat-1 data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full',
    // closed:
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full',
    // in between:
    'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[swipe=end]:animate-out',
  ].join(' '),
  {
    variants: {
      type: {
        background: 'border bg-background text-foreground',
        error: 'destructive group border-destructive bg-destructive-foreground text-destructive',
        success: 'success group border-success bg-success-foreground text-success',
        info: 'info group border-info bg-info-foreground text-info',
        warning: 'warning group border-warning bg-warning-foreground text-warning',
        primary: 'primary group border-primary bg-primary-foreground text-primary',
      },
    },
    defaultVariants: {
      type: 'info',
    },
  },
);

export type ToastProps = {
  type?: ToastVariants;
  children: ReactNode;
  // isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function ToastViewport() {
  return createPortal(
    <ToastPrimitives.Viewport className="fixed right-0 top-0 z-[100] flex max-h-screen min-w-[220px] max-w-[420px] flex-col gap-2 p-4 phone:top-0 phone:w-full phone:flex-col-reverse" />,
    document.body,
  );
}

export function Toast({ type, ...props }: ToastProps) {
  return <ToastPrimitives.Root open className={toastVariants({ type })} {...props} />;
}

type ActionProps = {
  className?: string;
  children: ReactNode;
  type: ToastVariants;
};

// revisit design later
export function ToastAction({ className, type, ...props }: ActionProps) {
  return (
    <ToastPrimitives.Action
      altText="action"
      onClick={() => alert('oi')}
      className={cn(
        'ml-auto inline-flex shrink-0 items-center justify-center rounded-md border bg-transparent px-2 text-sm font-medium transition-colors hover:bg-secondary',
        toastClassesByType[type].actionButton,
        'disabled:pointer-events-none disabled:opacity-50',
        'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        className,
      )}
      {...props}
    />
  );
}

export function CloseToast({ type }: { type: ToastVariants }) {
  return (
    <ToastPrimitives.Close
      toast-close=""
      className={cn(
        'absolute right-1 top-1.5 rounded-md p-1 text-foreground/80 opacity-100 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100',
        toastClassesByType[type].closeButton,
      )}
    >
      <XIcon size={14} />
    </ToastPrimitives.Close>
  );
}

export function ToastTitle({ children }: { children: ReactNode }) {
  return (
    <ToastPrimitives.Title className="text-sm font-semibold">{children}</ToastPrimitives.Title>
  );
}

export function ToastDescription({ children }: { children: ReactNode }) {
  return (
    <ToastPrimitives.Description className="text-xs opacity-90">
      {children}
    </ToastPrimitives.Description>
  );
}
